/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import BaseControl from 'scripts/base-control';
import elementResizeDetectorMaker from 'element-resize-detector';
import { debounce } from 'lodash';

const resizeDetector = elementResizeDetectorMaker();
const DEBOUNCE_DELAY = 400;
const DEFAULT_BORDER_COLOR = '#fff';
const DEFAULT_TEXT_COLOR = '#676b75';
const DEFAULT_OPEN_CLASSNAME = 'is-open';

export const InformationalHub = BaseControl.extend({
    init: function () {
        this._super();
        this.$topics = this.element.find('.js-card');
        this.$topicsContent = this.element.find('.js-card-content');
        this.$dropdowns = this.element.find('.js-dropdown');
        this.$subLinksContainer = this.element.find('.js-sub-link-container');
        this.$subLinks = this.element.find('.js-sub-items');
        this.$topicDescriptions = this.element.find('.js-description');

        this.initResizeListener();
        this.initEventListeners();
    },

    initEventListeners: function() {
        this.$topics.on('click', ({ currentTarget }) => this.handleTopicClick($(currentTarget)));
        this.$dropdowns.on('click', (e) => e.stopPropagation());
        this.$subLinksContainer.on('click', ({ currentTarget }) => this.handleTopicSubLinkContainerClick($(currentTarget)));

        this.options.appContext.model.screen.bind('screenSize', () => {
            this.$dropdowns.each((index, el) => {
                if ($(el).hasClass(DEFAULT_OPEN_CLASSNAME)) {
                    this.setDropdownPosition($(el).closest('.js-card'), $(el));
                }
            });
        });
    },

    initResizeListener: function() {
        resizeDetector.listenTo(this.$dropdowns, (el) => {
            const $el = $(el);
            if ($el.is(':hidden')) return;

            const height = $el.outerHeight() > 1 ? $el.outerHeight() : 0;
            this.$topics.css({marginBottom: 0});
            $el.closest('.js-card').css({marginBottom: `${height}px`})
        });
    },

    handleTopicClick: function($currentTarget) {
        if (this.$dropdowns.is(':animated')) return;

        const $currentDropdown = $currentTarget.find('.js-dropdown');
        const topicColor = $(this.options.topics).get($currentTarget.index()).colour;

        this.$subLinksContainer.removeClass(DEFAULT_OPEN_CLASSNAME);
        this.$subLinks.slideUp(DEBOUNCE_DELAY);
        this.$topicsContent.css({ borderColor: DEFAULT_BORDER_COLOR }).removeClass(DEFAULT_OPEN_CLASSNAME);
        this.$topicDescriptions.css({ color: DEFAULT_TEXT_COLOR });

        if ($currentDropdown.hasClass(DEFAULT_OPEN_CLASSNAME) || !($currentDropdown && $currentDropdown.length)) {
            return this.$dropdowns.removeClass(DEFAULT_OPEN_CLASSNAME).slideUp(DEBOUNCE_DELAY);
        }

        if (this.$dropdowns.hasClass(DEFAULT_OPEN_CLASSNAME)) {
            this.$dropdowns.removeClass(DEFAULT_OPEN_CLASSNAME).slideUp(DEBOUNCE_DELAY);
            debounce(() => this.setActiveCard($currentTarget), DEBOUNCE_DELAY)();
        } else {
            this.setActiveCard($currentTarget);
        }

        $currentTarget.find('.js-card-content').css({ borderColor: topicColor || DEFAULT_BORDER_COLOR});
        $currentTarget.find('.js-description').css({ color: topicColor || DEFAULT_TEXT_COLOR });
    },

    handleTopicSubLinkContainerClick: function($currentTarget) {
        if (!$currentTarget) return;

        $currentTarget.toggleClass(DEFAULT_OPEN_CLASSNAME);
        $currentTarget.find('.js-sub-items').slideToggle(DEBOUNCE_DELAY);
    },

    setActiveCard: function($currentTarget) {
        this.$dropdowns.each((index, el) => {
            if ($(el).closest('.js-card').index() === $currentTarget.index()) {
               this.setDropdownPosition($currentTarget, $(el));
            }
        });
    },

    setDropdownPosition: function($parent, $el) {
        if (!$el || !$parent) return;

        const $drodownArrow = $el.find('.js-dropdown-arrow');
        const top = $parent.position().top + $parent.outerHeight();
        const left = $parent.position().left + $parent.outerWidth() / 2;

        $drodownArrow.css({left: `${left.toFixed()}px`});
        $el.css({top: `${top}px`});
        $el.addClass(DEFAULT_OPEN_CLASSNAME).slideDown();
    },
});
